import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, X, Check, Folder } from "lucide-react";
import { apiService } from "../services/apiService";
import webAuthService from "../services/webAuthService";

const Groups = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupColor, setNewGroupColor] = useState("#3b82f6");
  const [editingGroup, setEditingGroup] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });

  const colorOptions = [
    { name: "Blue", value: "#3b82f6" },
    { name: "Emerald", value: "#10b981" },
    { name: "Purple", value: "#8b5cf6" },
    { name: "Rose", value: "#f43f5e" },
    { name: "Amber", value: "#f59e0b" },
    { name: "Cyan", value: "#06b6d4" },
    { name: "Pink", value: "#ec4899" },
    { name: "Indigo", value: "#6366f1" },
  ];

  useEffect(() => {
    initializeUser();
  }, []);

  const initializeUser = async () => {
    try {
      const uid = await webAuthService.getUserId();
      if (uid) {
        setUserId(uid);
        await fetchGroups(uid);
      }
    } catch (error) {
      console.error("Error initializing user:", error);
      setStatus({ type: "error", message: "Failed to initialize user" });
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async (uid) => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/groups?user_id=${uid}`);
      const data = await response.json();

      if (data.status === "success") {
        setGroups(data.groups || []);
      } else {
        throw new Error(data.message || "Failed to fetch groups");
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setStatus({ type: "error", message: "Failed to load groups" });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setStatus({ type: "error", message: "Group name cannot be empty" });
      return;
    }

    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newGroupName,
          description: newGroupDescription,
          color: newGroupColor,
          user_id: userId,
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        setStatus({ type: "success", message: "Group created successfully!" });
        setNewGroupName("");
        setNewGroupDescription("");
        setNewGroupColor("#3b82f6");
        setShowCreateModal(false);
        await fetchGroups(userId);
      } else {
        throw new Error(data.message || "Failed to create group");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      setStatus({ type: "error", message: error.message });
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editingGroup.name.trim()) {
      setStatus({ type: "error", message: "Group name cannot be empty" });
      return;
    }

    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/groups/${editingGroup.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editingGroup.name,
          description: editingGroup.description || "",
          color: editingGroup.color || "#3b82f6",
          user_id: userId,
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        setStatus({ type: "success", message: "Group updated successfully!" });
        setEditingGroup(null);
        setShowEditModal(false);
        await fetchGroups(userId);
      } else {
        throw new Error(data.message || "Failed to update group");
      }
    } catch (error) {
      console.error("Error updating group:", error);
      setStatus({ type: "error", message: error.message });
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (
      !confirm(
        "Are you sure you want to delete this group? Meetings will not be deleted, but they will be ungrouped."
      )
    ) {
      return;
    }

    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(
        `${baseURL}/groups/${groupId}?user_id=${userId}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setStatus({ type: "success", message: "Group deleted successfully!" });
        await fetchGroups(userId);
      } else {
        throw new Error(data.message || "Failed to delete group");
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      setStatus({ type: "error", message: error.message });
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F5F3FF" }}
      >
        <div className="flex flex-col items-center justify-center py-20">
          <div
            className="w-12 h-12 border-4 border-purple-200 rounded-full animate-spin"
            style={{ borderTopColor: "#e04665" }}
          ></div>
          <p className="mt-4 text-lg font-medium text-gray-700">
            Loading groups...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F3FF" }}>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Folder className="w-8 h-8" style={{ color: "#e04665" }} />
            Meeting Groups
          </h1>
          <p className="text-gray-600">
            Organize your meetings by client or project
          </p>
        </div>

        {/* Status Message */}
        {status.message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              status.type === "success"
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-red-50 border border-red-200 text-red-700"
            }`}
          >
            <span className="text-sm">{status.message}</span>
            <button
              onClick={() => setStatus({ type: "", message: "" })}
              className="ml-auto text-gray-600 hover:text-gray-800"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Create Button - Only show when groups exist */}
        {groups.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-white rounded-lg transition-all font-medium flex items-center gap-2 shadow-sm hover:shadow-md"
              style={{ backgroundColor: "#e04665" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#e63950")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e04665")
              }
            >
              <Plus size={18} />
              Create New Group
            </button>
          </div>
        )}

        {/* Groups List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.length === 0 ? (
            <div className="col-span-full bg-white border border-purple-200 rounded-xl p-12 text-center shadow-sm">
              <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-gray-800 font-semibold mb-2">
                No Groups Yet
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Create your first group to organize meetings
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 text-white rounded-lg transition-all font-medium text-sm inline-flex items-center gap-2 shadow-sm hover:shadow-md"
                style={{ backgroundColor: "#e04665" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e63950")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e04665")
                }
              >
                <Plus size={16} />
                Create Group
              </button>
            </div>
          ) : (
            groups.map((group) => (
              <div
                key={group.id}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all group cursor-pointer"
                style={{ borderLeftColor: group.color, borderLeftWidth: "4px" }}
                onClick={() => navigate(`/groups/${group.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${group.color}20` }}
                    >
                      <Folder
                        className="w-5 h-5"
                        style={{ color: group.color }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="text-gray-800 font-semibold transition-colors truncate"
                        style={{ color: "inherit" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#e04665")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "inherit")
                        }
                      >
                        {group.name}
                      </h3>
                      <p className="text-gray-600 text-xs">
                        {group.meeting_count || 0} meeting
                        {group.meeting_count !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                </div>

                {group.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {group.description}
                  </p>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingGroup(group);
                      setShowEditModal(true);
                    }}
                    className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group.id);
                    }}
                    className="flex-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>

                {/* View Meetings Indicator */}
                <div className="mt-3 pt-3 border-t border-slate-700/50">
                  <span className="text-blue-400 text-sm font-medium group-hover:text-blue-300">
                    View Meetings →
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white border border-purple-200 rounded-2xl p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Create New Group
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && !e.shiftKey && handleCreateGroup()
                    }
                    placeholder="Enter group name (e.g., Client Name)"
                    className="w-full px-4 py-3 bg-white border border-purple-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-2 block">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    placeholder="Brief description of this group..."
                    className="w-full px-4 py-3 bg-white border border-purple-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-2 block">
                    Color Tag
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setNewGroupColor(color.value)}
                        className={`h-12 rounded-lg border-2 transition-all ${
                          newGroupColor === color.value
                            ? "border-gray-800 scale-105"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {newGroupColor === color.value && (
                          <Check className="w-5 h-5 text-white mx-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewGroupName("");
                    setNewGroupDescription("");
                    setNewGroupColor("#3b82f6");
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2"
                >
                  <Check size={16} />
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingGroup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white border border-purple-200 rounded-2xl p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Edit Group
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={editingGroup.name}
                    onChange={(e) =>
                      setEditingGroup({ ...editingGroup, name: e.target.value })
                    }
                    onKeyPress={(e) =>
                      e.key === "Enter" && !e.shiftKey && handleUpdateGroup()
                    }
                    placeholder="Enter group name"
                    className="w-full px-4 py-3 bg-white border border-purple-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-2 block">
                    Description (Optional)
                  </label>
                  <textarea
                    value={editingGroup.description || ""}
                    onChange={(e) =>
                      setEditingGroup({
                        ...editingGroup,
                        description: e.target.value,
                      })
                    }
                    placeholder="Brief description of this group..."
                    className="w-full px-4 py-3 bg-white border border-purple-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-2 block">
                    Color Tag
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        onClick={() =>
                          setEditingGroup({
                            ...editingGroup,
                            color: color.value,
                          })
                        }
                        className={`h-12 rounded-lg border-2 transition-all ${
                          (editingGroup.color || "#3b82f6") === color.value
                            ? "border-gray-800 scale-105"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {(editingGroup.color || "#3b82f6") === color.value && (
                          <Check className="w-5 h-5 text-white mx-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingGroup(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateGroup}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2"
                >
                  <Check size={16} />
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Groups;
